import { Controller } from "@hotwired/stimulus"

const ADVANCE_KEY = "paperwork.board.openNextTask"

export default class extends Controller {
  static targets = ["link", "advance"]

  connect() {
    this.boundActioned = this.actioned.bind(this)
    this.boundClosed = () => {
      if (this.currentProcessReference) {
        this.element.querySelector(`.kanban-card[data-reference="${CSS.escape(this.currentProcessReference)}"] a`)?.focus({ preventScroll: true })
      }
      this.currentReference = null
    }
    window.addEventListener("task-actioned", this.boundActioned)
    document.addEventListener("resource-preview:closed", this.boundClosed)
    // The preference lives in a menu now, so it has to survive a reload or
    // nobody would find it switched on twice.
    if (this.hasAdvanceTarget) {
      try { this.advanceTarget.checked = localStorage.getItem(ADVANCE_KEY) === "true" } catch (_) {}
    }
  }

  rememberAdvance(event) {
    try { localStorage.setItem(ADVANCE_KEY, event.currentTarget.checked.toString()) } catch (_) {}
  }

  disconnect() {
    window.removeEventListener("task-actioned", this.boundActioned)
    document.removeEventListener("resource-preview:closed", this.boundClosed)
  }

  open(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    event.stopPropagation()
    this.openLink(event.currentTarget)
  }

  openLink(link) {
    this.currentReference = link.dataset.taskReference
    this.currentProcessReference = link.closest('.kanban-card').dataset.reference
    this.currentColumnKey = link.closest('[data-board-column-key]').dataset.boardColumnKey
    document.dispatchEvent(new CustomEvent("resource-preview:open", {
      detail: { url: link.dataset.previewUrl, title: (link.querySelector(".inbox-row__title") || link).textContent.trim() }
    }))
  }

  navigate(event) {
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
    const link = event.target.closest('[data-board-task-target="link"]')
    if (!link) return
    if (event.key === " ") {
      const checkbox = link.closest('.kanban-card').querySelector('input[type="checkbox"]')
      if (checkbox) { event.preventDefault(); checkbox.click() }
      return
    }
    if (!["ArrowDown", "ArrowUp"].includes(event.key)) return
    event.preventDefault()
    const links = Array.from(link.closest('turbo-frame').querySelectorAll('[data-board-task-target="link"]'))
    const next = links[links.indexOf(link) + (event.key === "ArrowDown" ? 1 : -1)]
    next?.focus()
  }

  async actioned() {
    if (!this.currentReference || this.refreshing) return
    const reference = this.currentReference
    const processReference = this.currentProcessReference
    const columnKey = this.currentColumnKey
    const oldLinks = this.linkTargets
    const following = oldLinks.slice(oldLinks.findIndex(link => link.dataset.taskReference === reference) + 1)
      .filter(link => link.closest('[data-board-column-key]').dataset.boardColumnKey === columnKey)
      .map(link => link.dataset.taskReference)
    const work = this.application.getControllerForElementAndIdentifier(this.element, "board-work")
    this.refreshing = true
    try {
      const refreshed = await work.refresh()
      // A close or another preview during the request takes precedence.
      if (!refreshed || this.currentReference !== reference) return
      if (this.advanceTarget.checked) {
        const links = this.linkTargets.filter(link => link.dataset.taskReference !== reference)
        const next = links.find(link => link.closest('.kanban-card').dataset.reference === processReference) ||
          following.map(reference => links.find(link => link.dataset.taskReference === reference)).find(Boolean)
        if (next) {
          this.openLink(next)
          return
        }
      }
      document.dispatchEvent(new CustomEvent("resource-preview:close"))
    } finally {
      this.refreshing = false
    }
  }
}
